import {
  MOBILE_OS,
  SUPPORTED_BROWSERS,
  APPLE_APP_LINK_PATHS
} from '@strava/constants';
import { isString } from 'lodash-es';

/**
 * Checks to see if device is mobile
 * @param {string} userAgent - user agent string for the current browser. For next-js apps (SSR),
 *       this value can be retrieved from request.headers['user-agent']
 * @returns {boolean} - true if the device is mobile
 */
export const isMobile = (
  userAgent = typeof window === 'undefined'
    ? null
    : window?.navigator?.userAgent
) => {
  if (isString(userAgent)) {
    // ref - https://developer.mozilla.org/en-US/docs/Web/HTTP/Browser_detection_using_the_user_agent#mobile_tablet_or_desktop
    return userAgent.toLowerCase().includes('mobi');
  }
  return false;
};

/**
 * Determines the OS for mobile devices
 * @param {string} userAgent - user agent string for the current browser. For next-js apps (SSR),
 *       this value can be retrieved from request.headers['user-agent']
 * @returns {(string|null)} - one of ['android', 'ipad', 'iphone'] or null for unknown
 */
export const getMobileOS = (
  userAgent = typeof window === 'undefined'
    ? null
    : window?.navigator?.userAgent
) => {
  if (isString(userAgent)) {
    if (userAgent.includes('Android')) {
      return MOBILE_OS.android;
    }
    if (userAgent.includes('iPad')) {
      return MOBILE_OS.iPad;
    }
    if (userAgent.includes('iPhone')) {
      return MOBILE_OS.iPhone;
    }
  }
  return null;
};

/**
 * Determines, in a limited fashion, the browser according to the user agent.
 * NOTE: UAs are notoriously difficult to parse. This is meant as a bare-bones, noncomprehensive parser,
 * based on https://www.whatismybrowser.com/guides/the-latest-user-agent/.
 *
 * The order of conditionals is important, bc some Chrome & Firefox UAs include 'Safari', too.
 * Some Edge UAs may be recognized as Chrome for now, since they can include 'Chrome'. We don't
 * officially support Edge (yet?).
 *
 * @param {string} userAgent - user agent string for the current browser. For next-js apps (SSR),
 *       this value can be retrieved from request.headers['user-agent']
 * @returns {(string|null)} - one of ['chrome', 'firefox', 'safari'] or null for unknown/unsupported browser
 */
export const getBrowser = (userAgent = window?.navigator?.userAgent) => {
  if (isString(userAgent)) {
    if (userAgent.includes('Chrome') || userAgent.includes('CriOS')) {
      return SUPPORTED_BROWSERS.chrome;
    }
    if (userAgent.includes('Firefox') || userAgent.includes('FxiOS')) {
      return SUPPORTED_BROWSERS.firefox;
    }
    if (userAgent.includes('Safari')) {
      return SUPPORTED_BROWSERS.safari;
    }
  }

  return null;
};

/**
 * Universal links are determined by the paths described in `apple-app-site-association` in active.
 * @param {string} pathname - window.location.pathname or nextjs equivalent
 * @returns {boolean} - whether the pathname string matches
 */
export const isUniversalLinkPage = (pathname: string) => {
  if (isString(pathname)) {
    return APPLE_APP_LINK_PATHS.some((path: string) => {
      const pathWithoutAsterisk = path.replace('/*', '');
      return pathname.startsWith(pathWithoutAsterisk);
    });
  }
  return false;
};

/**
 * Checks to see if localStorage is available
 *
 * @return {boolean} - true if localStorage can be used
 */
export const isLocalStorageAvailable = () => {
  const test = 'test';
  try {
    localStorage.setItem(test, test);
    localStorage.removeItem(test);
    return true;
  } catch {
    return false;
  }
};

/**
 * Checks to see if sessionStorage is available
 *
 * @return {boolean} - true if sessionStorage can be used
 */
export const isSessionStorageAvailable = () => {
  const test = 'test';
  try {
    sessionStorage.setItem(test, test);
    sessionStorage.removeItem(test);
    return true;
  } catch {
    return false;
  }
};
